import React, { useEffect, useId, useRef, useState } from 'react'

import { ChartView, formatValue } from './ChartView'

/**
 * The baby name lookup chart: any name in the Social Security file, girls or
 * boys, from 1880 to the newest year. The page's own data (`chart.data`) is the
 * featured name below; every other name comes from GET /baby-names/:name.json
 * (BabyNamesController). `?name=Liam&sex=boys` in the address opens a name
 * directly, and the address follows each search so a result can be shared.
 */

const FEATURED = { name: 'Emma', sex: 'girls' } // keep in step with the chart.yml refresh options
const SEXES = ['girls', 'boys']

function toPoints(counts, firstYear) {
  return counts.map((value, index) => ({ label: String(firstYear + index), value }))
}

async function getJson(path, signal) {
  const response = await fetch(path, { headers: { Accept: 'application/json' }, signal })
  if (response.status === 404) return null
  if (!response.ok) throw new Error(`Request failed (${response.status})`)
  return response.json()
}

export function BabyNameLookup({ chart }) {
  const inputId = useId()
  const listId = useId()
  const featured = { name: FEATURED.name, girls: null, boys: null, [FEATURED.sex]: chart.data }
  const [result, setResult] = useState(featured)
  const [sex, setSex] = useState(FEATURED.sex)
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [status, setStatus] = useState({ loading: false, message: null })
  const lookupRef = useRef(null)

  const lookUp = async (rawName, preferredSex) => {
    const name = rawName.trim()
    if (!name) return
    lookupRef.current?.abort()
    const controller = new AbortController()
    lookupRef.current = controller
    setStatus({ loading: true, message: null })
    try {
      const found = await getJson(`/baby-names/${encodeURIComponent(name)}.json`, controller.signal)
      if (!found) {
        setStatus({
          loading: false,
          message: `No baby named “${name}” is in the data. It only includes names given to at least 5 babies of one sex in a year.`,
        })
        return
      }
      const next = {
        name: found.name,
        girls: found.girls ? toPoints(found.girls, found.first_year) : null,
        boys: found.boys ? toPoints(found.boys, found.first_year) : null,
      }
      const total = (points) => (points ?? []).reduce((sum, p) => sum + p.value, 0)
      const chosen = next[preferredSex] ? preferredSex : total(next.girls) >= total(next.boys) ? 'girls' : 'boys'
      setResult(next)
      setSex(chosen)
      setStatus({ loading: false, message: null })
      const url = new URL(window.location.href)
      url.searchParams.set('name', found.name)
      url.searchParams.set('sex', chosen)
      window.history.replaceState(null, '', url)
    } catch (error) {
      if (error.name === 'AbortError') return
      setStatus({ loading: false, message: 'That name couldn’t be loaded. Please try again.' })
    }
  }

  // Open the name in the address, if any.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('name')) lookUp(params.get('name'), params.get('sex'))
    return () => lookupRef.current?.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Suggest names that start with what has been typed.
  useEffect(() => {
    const prefix = query.trim()
    if (prefix.length < 2) {
      setSuggestions([])
      return undefined
    }
    const controller = new AbortController()
    const timer = setTimeout(() => {
      getJson(`/baby-names.json?q=${encodeURIComponent(prefix)}`, controller.signal)
        .then((body) => setSuggestions(body?.names ?? []))
        .catch(() => {})
    }, 200)
    return () => { clearTimeout(timer); controller.abort() }
  }, [query])

  const points = result[sex] ?? []
  const peak = points.reduce((best, p) => (p.value > (best?.value ?? -1) ? p : best), null)
  const latest = points[points.length - 1]
  const setSexAndUrl = (value) => {
    setSex(value)
    const url = new URL(window.location.href)
    url.searchParams.set('name', result.name)
    url.searchParams.set('sex', value)
    window.history.replaceState(null, '', url)
  }

  return (
    <div className="w-full">
      <form
        className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end"
        onSubmit={(event) => { event.preventDefault(); lookUp(query) }}
      >
        <div className="min-w-0 flex-1">
          <label htmlFor={inputId} className="form-label">Find a name</label>
          <input
            id={inputId}
            type="search"
            className="input-form-field"
            placeholder="Type a name, e.g. Liam or Jordan"
            autoComplete="off"
            list={listId}
            value={query}
            maxLength={20}
            onChange={(event) => setQuery(event.target.value)}
          />
          <datalist id={listId}>
            {suggestions.map((name) => <option key={name} value={name} />)}
          </datalist>
        </div>
        <button type="submit" className="btn-primary min-h-10" disabled={status.loading}>
          {status.loading ? 'Loading…' : 'Show'}
        </button>
      </form>

      {status.message ? <p className="alert-info mb-4" role="status">{status.message}</p> : null}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="font-semibold text-ink break-words">
          {result.name}, {sex}
          {peak && peak.value > 0 ? (
            <span className="block text-sm font-normal text-ink-muted">
              Peak: {formatValue(peak.value)} babies in {peak.label}. In {latest.label}: {formatValue(latest.value)}.
            </span>
          ) : null}
        </p>
        <div className="flex gap-2" role="group" aria-label="Girls or boys">
          {SEXES.map((value) => (
            <button
              key={value}
              type="button"
              className={`${sex === value ? 'btn-primary' : 'btn-secondary'} btn-sm min-h-10 capitalize`}
              aria-pressed={sex === value}
              disabled={!result[value]}
              onClick={() => setSexAndUrl(value)}
            >
              {value}
            </button>
          ))}
        </div>
      </div>

      <ChartView
        searchable={false}
        chart={{ ...chart, title: `Babies named ${result.name} each year (${sex})`, data: points }}
      />

      <details className="mt-4 text-sm">
        <summary className="cursor-pointer font-semibold text-primary">The numbers for {result.name} ({sex})</summary>
        <div className="mt-2 max-h-80 overflow-y-auto">
          <table className="admin-table w-full">
            <thead>
              <tr>
                <th scope="col" className="text-left">Year</th>
                <th scope="col" className="text-right">Babies</th>
              </tr>
            </thead>
            <tbody>
              {[...points].reverse().map((p) => (
                <tr key={p.label}>
                  <td>{p.label}</td>
                  <td className="text-right tabular-nums">{p.value ? formatValue(p.value) : 'Fewer than 5'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  )
}
